import React from "react";
import { connect } from "react-redux";

import { faCalendarCheck } from "@fortawesome/pro-solid-svg-icons/faCalendarCheck";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Col, Row } from "antd/es/grid";
import { isSameDay } from "date-fns";
import { useIntl } from "react-intl";

import { EntityMentionContent } from "@app/component/EntityMentions";
import FormattedLongContentParagraph from "@app/component/typography/FormattedLongContentParagraph";
import LongContentParagraph from "@app/component/typography/LongContentParagraph";
import MmcNotificationAction from "@app/enum/MmcNotificationAction";
import MmcNotificationCategory from "@app/enum/MmcNotificationCategory";
import {
  getAllTeamsOfEntireOrganization,
  getAllUsersOfEntireOrganization,
} from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import MmcNotification from "@app/types/MmcNotification";
import { formatDate } from "@app/util/formatters";
import isValidDate from "@app/util/isValidDate";
import { parseApiDate } from "@app/util/parsers";

import styles from "../NotificationCommon.module.scss";

interface TitleProps {
  notification: MmcNotification;
}

const Title = ({ notification, teams, users }: TitleProps & ReturnType<typeof mapStateToProps>) => {
  const {
    action,
    category,
    coachingNote,
    coachingTask,
    entity,
    group,
    guides,
    isDefaultGuideAssignment,
    note,
    route,
  } = notification;
  const intl = useIntl();
  const isPlural = entity && entity.length > 1;
  const entityName = entity && entity[0] ? entity[0].name : "";
  let text: React.ReactNode = entityName;

  // Task mentions arrive as MENTION + COACHING_TASK category (same shape as note
  // mentions), not the dedicated COACHING_TASK_MENTIONED action — handle both.
  const isCoachingTaskMention =
    action === MmcNotificationAction.COACHING_TASK_MENTIONED ||
    (action === MmcNotificationAction.MENTION &&
      category === MmcNotificationCategory.COACHING_TASK);

  // Every task notification renders the same card (name + description + due date).
  // The mention check must stay ahead of the note branch below so MENTION +
  // COACHING_TASK isn't swallowed by the generic MENTION handling.
  const isCoachingTask =
    isCoachingTaskMention ||
    [
      MmcNotificationAction.COACHING_TASK_ASSIGNED,
      MmcNotificationAction.COACHING_TASK_COMPLETED,
      MmcNotificationAction.COACHING_TASK_CREATED,
      MmcNotificationAction.COACHING_TASK_OVERDUE,
    ].includes(action);

  if (action === MmcNotificationAction.ACTIVITY_LOGGED) {
    text = (
      <Row gutter={layout.spacerS}>
        <Col>
          <FontAwesomeIcon icon={faCalendarCheck} />
        </Col>
        <Col>{entityName}</Col>
      </Row>
    );
  } else if (isCoachingTask) {
    const dueDate = coachingTask?.startAt ? parseApiDate(coachingTask.startAt) : undefined;
    const taskName = coachingTask?.name ?? entity?.[0]?.name;
    text = (
      <LongContentParagraph className={styles.mentionWrapper} noMargin>
        {taskName ? <div className={styles.taskTitle}>{taskName}</div> : null}
        {coachingTask?.note ? (
          <div className={styles.description}>
            {/* Task notes carry @entity markers + mentions[] (same shape as coaching
                notes), so render feed-style pills per line. */}
            {coachingTask.note.split("\n").map((line, key) => (
              <React.Fragment key={key}>
                <EntityMentionContent
                  allUsers={users}
                  content={line}
                  mentions={coachingTask.mentions}
                />
                <br />
              </React.Fragment>
            ))}
          </div>
        ) : null}
        {dueDate && isValidDate(dueDate) ? (
          <div className={styles.description}>
            {intl.formatMessage(
              {
                id: "navbar.notifications.list.notification.title.coaching.taskDueDate",
                defaultMessage: "Due: {dueDate}",
                description: "Navbar Notifications - list - notification - coaching task due date",
              },
              { dueDate: formatDate(dueDate, "PP") }
            )}
          </div>
        ) : null}
      </LongContentParagraph>
    );
  } else if (
    [
      MmcNotificationAction.ACTIVITY_NOTE_ADDED,
      MmcNotificationAction.ACTIVITY_NOTE_UPDATED,
      MmcNotificationAction.COACHING_NOTE_CREATED,
      MmcNotificationAction.COACHING_NOTE_REPLIED,
      MmcNotificationAction.MENTION,
      MmcNotificationAction.NOTE_ADDED,
    ].includes(action)
  ) {
    // Coaching notes carry entity markers + mentions[], so render feed-style
    // pills (split per line since EntityMentionContent ignores newlines).
    // Regular notes use Lexer for @email/@team markers.
    text = coachingNote ? (
      <LongContentParagraph className={styles.mentionWrapper} noMargin>
        {(coachingNote.note ?? "").split("\n").map((line, key) => (
          <React.Fragment key={key}>
            <EntityMentionContent
              allUsers={users}
              content={line}
              mentions={coachingNote.mentions}
            />
            <br />
          </React.Fragment>
        ))}
      </LongContentParagraph>
    ) : (
      <FormattedLongContentParagraph
        className={styles.mentionWrapper}
        lexerProps={{ mentionClassName: styles.mention, teams, users }}
        noMargin
      >
        {note?.note ?? ""}
      </FormattedLongContentParagraph>
    );
  } else if (
    [
      MmcNotificationAction.COMPANY_GROUP_SHARED,
      MmcNotificationAction.DEAL_GROUP_SHARED,
      MmcNotificationAction.PEOPLE_GROUP_SHARED,
    ].includes(action)
  ) {
    text = group?.name ?? "";
  } else if (
    [
      MmcNotificationAction.COMPANY_ROUTE_SHARED,
      MmcNotificationAction.PEOPLE_ROUTE_SHARED,
      MmcNotificationAction.UNIFIED_ROUTE_SHARED,
    ].includes(action)
  ) {
    text = route?.name ?? "";
  } else if (action === MmcNotificationAction.OUT_OF_CADENCE && isPlural) {
    text = intl.formatMessage({
      id: "navbar.notifications.list.notification.title.seeRecordsOutOfCadence",
      defaultMessage: "See records that are past due",
      description: "Navbar Notifications - list - notification - See records out of cadence",
    });
  } else if (action === MmcNotificationAction.OVERDUE_ACTIVITIES && isPlural) {
    text = intl.formatMessage({
      id: "navbar.notifications.list.notification.title.seeOverdueActivities",
      defaultMessage: "See overdue activities",
      description: "Navbar Notifications - list - notification - See overdue activities",
    });
  } else if (action === MmcNotificationAction.DEALS_ROTTING && isPlural) {
    text = intl.formatMessage({
      id: "navbar.notifications.list.notification.title.seeRottingDeals",
      defaultMessage: "See rotting deals",
      description: "Navbar Notifications - list - notification - See rotting deals",
    });
  } else if (action === MmcNotificationAction.DEALS_MARKED_WON && isPlural) {
    text = intl.formatMessage({
      id: "navbar.notifications.list.notification.title.seeAllClosedWonDeals",
      defaultMessage: "See all Closed Won Deals",
      description: "Navbar Notifications - list - notification - See all Closed Won Deals",
    });
  } else if (action === MmcNotificationAction.DEALS_MARKED_LOST && isPlural) {
    text = intl.formatMessage({
      id: "navbar.notifications.list.notification.title.seeAllClosedLostDeals",
      defaultMessage: "See all Closed Lost Deals",
      description: "Navbar Notifications - list - notification - See all Closed Lost Deals",
    });
  } else if (action === MmcNotificationAction.DEALS_STAGE_CHANGED && isPlural) {
    text = intl.formatMessage({
      id: "navbar.notifications.list.notification.title.seeAllDeals",
      defaultMessage: "See all Deals",
      description: "Navbar Notifications - list - notification - See all Deals",
    });
  } else if (action === MmcNotificationAction.MARK_ACTIVITY_AS_DONE) {
    text = intl.formatMessage({
      id: "navbar.notifications.list.notification.title.mark_activity_as_done",
      defaultMessage: 'Open to mark as "Done"',
      description: "Navbar Notifications - list - notification - Open to mark as Done",
    });
  } else if (
    [
      MmcNotificationAction.OWNED_DEALS_CLOSING,
      MmcNotificationAction.SHARED_DEALS_CLOSING,
    ].includes(action) &&
    isPlural
  ) {
    text = intl.formatMessage({
      id: "navbar.notifications.list.notification.title.seeDeals",
      defaultMessage: "See Deals",
      description: "Navbar Notifications - list - notification  - See Deals",
    });
  } else if (MmcNotificationAction.INCOMPLETE_ACTIVITY === action) {
    text = intl.formatMessage({
      id: "navbar.notifications.list.notification.title.openToMarkItAsComplete",
      defaultMessage: 'Open to mark as "Done"',
      description: "Navbar Notifications - list - notification  - Open to mark as Done",
    });
  } else if (
    [MmcNotificationAction.GUIDE_ASSIGNED, MmcNotificationAction.GUIDE_REMINDER].includes(action)
  ) {
    if (isDefaultGuideAssignment && guides?.length) {
      text = guides[0].displayName;
    } else {
      text = intl.formatMessage(
        {
          id: "navbar.notifications.list.notification.title.seeGuides",
          defaultMessage: "See {count, plural, one {guide} other {guides}}",
          description: "Navbar Notifications - list - notification  - See Guides",
        },
        { count: guides?.length ?? 0 }
      );
    }
  } else if (
    [
      MmcNotificationAction.AUTO_TRACKED_TRIPS,
      MmcNotificationAction.TRIP_FLAGGED,
      MmcNotificationAction.UNCLASSIFIED_TRIPS,
    ].includes(action)
  ) {
    if (action === MmcNotificationAction.AUTO_TRACKED_TRIPS && !notification.count) {
      text = intl.formatMessage({
        id: "navbar.notifications.list.notification.title.reviewPermissions",
        defaultMessage: "Review device permissions on",
        description: "Navbar Notifications - list - notification - Review permissions",
      });
    } else if (action === MmcNotificationAction.TRIP_FLAGGED) {
      const startTimeString = notification.entity?.[0]?.startTime;
      const startDate = startTimeString ? parseApiDate(startTimeString) : undefined;

      if (!isValidDate(startDate)) {
        text = <></>;
      }

      const endTimeString = notification.entity?.[0]?.endTime;
      const endDate = endTimeString ? parseApiDate(endTimeString) : undefined;
      if ((!endTimeString || !isValidDate(endDate)) && startDate) {
        text = formatDate(startDate, "PPp");
      }

      if (startDate && endDate) {
        const formattedStartDate = startDate ? formatDate(startDate, "PPp") : undefined;

        text = intl.formatMessage(
          {
            id: "navbar.notifications.list.notification.title.tripDuration",
            defaultMessage: "{startDateTime} - {endDateTime}",
            description: "Navbar Notifications - list - notification - Trip duration",
          },
          {
            endDateTime: formatDate(endDate, isSameDay(startDate, endDate) ? "p" : "PPp"),
            startDateTime: formattedStartDate,
          }
        );
      }
    } else {
      text = intl.formatMessage({
        id: "navbar.notifications.list.notification.title.review",
        defaultMessage: "Review",
        description: "Navbar Notifications - list - notification - Review",
      });
    }
  } else if (
    [
      MmcNotificationAction.EMAIL_TEMPLATE_SHARED,
      MmcNotificationAction.FOLDER_SHARED,
      MmcNotificationAction.RESOURCE_SHARED,
    ].includes(action)
  ) {
    // No text required
    text = "";
  } else if (action === MmcNotificationAction.BOUNCED_EMAIL) {
    text = entity?.[0]?.subject;
  }
  return <div className={styles.title}>{text}</div>;
};

const mapStateToProps = (state: RootState) => ({
  teams: getAllTeamsOfEntireOrganization(state),
  users: getAllUsersOfEntireOrganization(state),
});

export default connect(mapStateToProps)(Title);
